import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button, Form, FormProps, Input, Switch } from "antd";
const { Password } = Input;
import { Link, useNavigate } from "react-router-dom";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import { authServices } from "~/services/auth";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";
import { getSupabase } from "~/lib/supabase";

type FieldType = {
  username: string;
  password: string;
};

export const LoginView = () => {
  const navigate = useNavigate();
  const { login, setMe } = useAuth();
  const api = useNotification();
  const [googleLoading, setGoogleLoading] = useState(false);

  const navigateByRole = (role: string) => {
    switch (role) {
      case Role.CLIENT:
        navigate("/overview");
        break;
      case Role.ADMIN || Role.SUPER_ADMIN:
        navigate("/");
        break;
      case Role.STAFF:
        navigate("/assignments");
        break;
    }
  };

  // Handle OAuth callback — detect Supabase session in URL hash on mount
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get("access_token");

      if (!accessToken) return;

      setGoogleLoading(true);
      try {
        // Exchange Supabase token for our backend JWT tokens
        const tokens = await authServices.googleLogin({ accessToken });
        login(tokens);
        const user = await authServices.getMe();
        setMe(user);
        navigateByRole(user.role);
      } catch (error: any) {
        api.error({
          message: "Google login failed",
          description:
            error.response?.data?.message ||
            "Could not sign in with Google. Please ensure your account exists.",
        });
      } finally {
        setGoogleLoading(false);
        // Clean the URL hash
        window.history.replaceState(null, "", window.location.pathname);
      }
    };

    handleOAuthCallback();
  }, []);

  const { mutate, isPending } = useMutation({
    mutationFn: authServices.login,
    onSuccess: async (tokens) => {
      login(tokens);
      const user = await authServices.getMe();
      setMe(user);
      navigateByRole(user.role);
    },
    onError: (error: any) => {
      api.error({
        message: "Login failed",
        description: error.response?.data?.message || "Invalid credentials",
      });
    },
  });

  const onFinish: FormProps<FieldType>["onFinish"] = (values) => {
    mutate(values);
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/login`,
        },
      });
      if (error) {
        api.error({
          message: "Google login failed",
          description: error.message,
        });
        setGoogleLoading(false);
      }
      // Browser will redirect — loading state stays on until redirect
    } catch {
      api.error({
        message: "Google login failed",
        description: "An unexpected error occurred",
      });
      setGoogleLoading(false);
    }
  };

  const isLoading = isPending || googleLoading;

  return (
    <main className="flex-1 flex flex-col items-center justify-center px-4">
      {/* Content */}
      <div className="max-w-6xl mx-auto flex flex-col items-center">
        {/* Brand Name - Variation 3 with App */}
        <div className="mb-12 flex items-center justify-center gap-3">
          <h1
            className="text-5xl sm:text-6xl text-black"
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase'
            }}
          >
            Art Handler
          </h1>
          <span
            className="text-lg sm:text-xl text-black"
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              alignSelf: 'flex-end',
              paddingBottom: '0.3em'
            }}
          >
            App
          </span>
        </div>

        {/* Sign In Card - Neo Brutalist Style */}
        <div className="w-full max-w-md">
          <div className="border-2 border-black bg-[#faf8f3] p-8 sm:p-10">

            <Form
              layout="vertical"
              onFinish={onFinish}
              className="space-y-6"
              requiredMark={CustomizeRequiredMark}
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: "Please input your username!" },
                ]}
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    User
                  </span>
                }
                className="mb-6"
              >
                <Input
                  size="large"
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  className="border-2 border-black px-3 py-2 font-bold text-sm uppercase bg-[#faf8f3] text-black placeholder:text-black/50 focus:border-black focus:ring-0"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  disabled={isLoading}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Password
                  </span>
                }
                rules={[{ required: true }]}
                className="mb-6"
              >
                <Password
                  size="large"
                  id="password"
                  placeholder="Enter your password"
                  className="border-2 border-black px-3 py-2 font-bold text-sm uppercase bg-[#faf8f3] text-black placeholder:text-black/50 focus:border-black focus:ring-0 [&_input]:font-bold [&_input]:text-sm [&_input]:bg-[#faf8f3] [&_input]:text-black [&_input]:placeholder:text-black/50 [&_input]:normal-case"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  disabled={isLoading}
                />
              </Form.Item>

              <div className="flex items-center pt-2 mb-6">
                <div className="flex items-center">
                  <Switch
                    id="remember"
                    className="[&_.ant-switch-inner]:bg-[#faf8f3] [&_.ant-switch-checked]:bg-black [&_.ant-switch-handle]:border-2 [&_.ant-switch-handle]:border-black"
                  />
                  <label
                    htmlFor="remember"
                    className="ml-3 text-sm font-bold uppercase tracking-wide text-black"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Remember me
                  </label>
                </div>
              </div>

              <Button
                loading={isPending}
                htmlType="submit"
                size="large"
                className="w-full border-2 border-black bg-black text-white px-4 py-3 font-bold text-sm uppercase tracking-wide hover:opacity-90 transition-all"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                disabled={googleLoading}
              >
                {isPending ? "Authenticating..." : "Sign In"}
              </Button>
            </Form>

            {/* Divider */}
            <div className="flex items-center my-6">
              <div className="flex-1 border-t-2 border-black/20" />
              <span
                className="px-4 text-xs font-bold uppercase tracking-widest text-black/50"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                or
              </span>
              <div className="flex-1 border-t-2 border-black/20" />
            </div>

            {/* Google Sign In Button */}
            <Button
              size="large"
              onClick={handleGoogleLogin}
              loading={googleLoading}
              disabled={isPending}
              className="w-full border-2 border-black bg-white text-black px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-black/5 transition-all flex items-center justify-center gap-3"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              icon={
                !googleLoading ? (
                  <svg width="18" height="18" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                ) : undefined
              }
            >
              {googleLoading ? "Connecting..." : "Sign in with Google"}
            </Button>

            <div className="text-center mt-6">
              <Link
                to="/request-password-reset"
                className="text-sm font-bold uppercase tracking-wide text-black hover:opacity-70 transition-opacity border-b-2 border-transparent hover:border-black"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Forgot Password?
              </Link>
            </div>

            <div className="text-center text-sm font-medium text-black pt-6 border-t-2 border-black mt-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Need help?{" "}
              <Link
                to="#"
                className="font-bold uppercase tracking-wide text-black hover:opacity-70 transition-opacity border-b-2 border-transparent hover:border-black"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};
